import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    IHaulierDetail,
    IHaulierSelectItem,
    HaulierCreatePayload,
    HaulierRestoreDeletedPayload,
    HaulierUpdatePayload,
} from "@/customTypings/haulier/Haulier";
import { authHeader, handleResponse } from "../../utils";
import { postHaulierCreate } from "@/utils/haulier/haulierCreateConflict";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

function siteHauliersBase(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/hauliers`;
}

export const hauliersService = {
    getById,
    select,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(siteId: string, id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`${siteHauliersBase(siteId)}/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IHaulierDetail>;
}

/** Active hauliers for the site (carrier pre-fill lookups). */
function select(siteId: string) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteHauliersBase(siteId)}/select`, requestOptions).then(handleResponse) as Promise<IHaulierSelectItem[]>;
}

function create(siteId: string, payload: HaulierCreatePayload) {
    return postHaulierCreate<IHaulierDetail>(siteId, payload);
}

function restoreDeletedWithCreateValues(siteId: string, payload: HaulierRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteHauliersBase(siteId)}/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IHaulierDetail>;
}

function update(siteId: string, payload: HaulierUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`${siteHauliersBase(siteId)}/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IHaulierDetail>>;
}

function disable(siteId: string, id: string) {
    return postQuery(siteId, "disable", id);
}

function enable(siteId: string, id: string) {
    return postQuery(siteId, "enable", id);
}

function softDelete(siteId: string, id: string) {
    return postQuery(siteId, "softdelete", id);
}

function restore(siteId: string, id: string) {
    return postQuery(siteId, "restore", id);
}

function postQuery(siteId: string, action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`${siteHauliersBase(siteId)}/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(
        handleResponse
    );
}
